import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Typography,
  Box,
  CircularProgress,
} from '@mui/material';
import { ConvergeNavigationUrls } from '../../../contexts';
import { NewConvergeButton } from '../../../presentation/components';

interface DialogBroker {
  uuid: string;
  name: string;
  brokerage: string;
  status: string;
}

interface FetchedBroker {
  uuid?: string;
  name?: string;
  brokerage?: string;
  status?: string;
}

interface OldFetchedBroker {
  id?: string;
  broker?: string;
  brokerage?: string;
  status?: string;
}

interface BrokersDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  testId: string;
  loading: boolean;
  error?: Error | string | null;
  fetchedBrokers?: FetchedBroker[];
  useNewQuery?: boolean;
  oldFetchedBrokers?: OldFetchedBroker[];
  noDataMessage: string;
}
export const BrokersDialog: React.FC<BrokersDialogProps> = ({
  open,
  onClose,
  title,
  testId,
  loading,
  error,
  fetchedBrokers,
  useNewQuery = true,
  oldFetchedBrokers,
  noDataMessage,
}) => {
  const [brokers, setBrokers] = useState<DialogBroker[]>([]);

  useEffect(() => {
    if (useNewQuery && fetchedBrokers && Array.isArray(fetchedBrokers)) {
      const dialogBrokers: DialogBroker[] = fetchedBrokers.map((broker) => ({
        uuid: broker.uuid || '',
        name: broker.name || '',
        brokerage: broker.brokerage || '',
        status: broker.status || '',
      }));
      setBrokers(dialogBrokers);
    } else if (
      !useNewQuery &&
      oldFetchedBrokers &&
      Array.isArray(oldFetchedBrokers)
    ) {
      const dialogBrokers: DialogBroker[] = oldFetchedBrokers.map((broker) => ({
        uuid: broker.id || '',
        name: broker.broker || '',
        brokerage: broker.brokerage || '',
        status: broker.status || '',
      }));
      setBrokers(dialogBrokers);
    }
  }, [useNewQuery, fetchedBrokers, oldFetchedBrokers]);

  const handleBrokerClick = (brokerUuid: string) => {
    window.location.href = ConvergeNavigationUrls.broker({
      brokerUuid,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      data-testid={testId}
      PaperProps={{
        sx: {
          maxHeight: '80vh',
          maxWidth: '430px',
          padding: '20px',
        },
      }}
    >
      <DialogTitle>
        <Typography
          variant="h4"
          component="div"
          sx={{
            fontWeight: 300,
            padding: '10px 34px',
          }}
        >
          {title}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            py={4}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box py={2}>
            <Typography color="error">
              Error loading brokers. Please try again.
            </Typography>
          </Box>
        ) : !loading && brokers.length === 0 ? (
          <Box py={2}>
            <Typography color="text.secondary">{noDataMessage}</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              border: '1px solid #cccccc',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <List sx={{ padding: 0 }}>
              {brokers.map((broker, index) => (
                <React.Fragment key={broker.uuid}>
                  <ListItem
                    data-testid={`broker-name_${index + 1}`}
                    onClick={() => handleBrokerClick(broker.uuid)}
                    sx={{
                      cursor: 'pointer',
                    }}
                  >
                    <ListItemText
                      sx={{
                        margin: 0,
                      }}
                      primary={
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            textDecoration: 'underline',
                          }}
                        >
                          {broker.name}
                        </Typography>
                      }
                    />
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <NewConvergeButton
          variant="outlined"
          onClick={onClose}
          label="Close"
          data-testid="close-dialog-button"
        />
      </DialogActions>
    </Dialog>
  );
};
